import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Keyboard } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAlertsInfinite } from './hooks/useAlertsInfinite'
import { useBulkSelection } from './hooks/useBulkSelection'
import { useQueueKeyboard } from './hooks/useQueueKeyboard'
import { useAlertsWebSocket } from './hooks/useAlertsWebSocket'
import { useSavedFilters } from './hooks/useSavedFilters'
import { AlertsTable } from './components/queue/AlertsTable'
import { FilterBar } from './components/queue/FilterBar'
import { SavedFilterTabs } from './components/queue/SavedFilterTabs'
import { BulkActionsBar } from './components/queue/BulkActionsBar'
import { LiveIndicator } from './components/queue/LiveIndicator'
import { NewAlertsToast } from './components/queue/NewAlertsToast'
import { ShortcutsModal } from './components/queue/ShortcutsModal'
import { ExportCsvButton } from './components/queue/ExportCsvButton'
import { SaveFilterDialog } from './components/queue/SaveFilterDialog'
import type { AlertFilters, SavedFilter, SortKey } from './types'

const DEFAULT_STATUS: AlertFilters['status'] = ['open', 'in_review']

/**
 * Builds the initial/re-seeded filter set from the `queue` URL param(s).
 *
 * `?queue=` is repeatable — `searchParams.getAll('queue')` collects every
 * occurrence, matching `AlertFilters.queue_id` being a `string[]` and the
 * OR semantics `buildSearchParams` (api.ts) already uses when serializing
 * `queue_id` back onto the wire. A single `?queue=<id>` link (the common
 * case, e.g. the sidebar's Fraud Queues badge or a Queue Dashboard card)
 * naturally collects to a one-element array.
 */
function filtersFromQueueParam(queueIds: readonly string[]): AlertFilters {
  return queueIds.length > 0
    ? { status: DEFAULT_STATUS, queue_id: [...queueIds] }
    : { status: DEFAULT_STATUS }
}

/**
 * AF-126 — alerts queue page.
 *
 * Composes saved-filter tabs + filter chip bar + virtualized table + bulk
 * actions bar + real-time indicator. Keyboard shortcuts (j/k/Enter/space/x/?)
 * scoped to this page via `useQueueKeyboard`.
 *
 * URL → filter binding (one-directional, `queue` param only):
 * The `queue` query param is the source of truth for the queue dimension —
 * it seeds `filters.queue_id` on mount AND re-seeds it whenever the param
 * changes while this page stays mounted (e.g. clicking a different Queue
 * Dashboard card without a full page navigation), so links are shareable
 * and the browser back/forward buttons behave. We deliberately do NOT mirror
 * other in-page filter edits (status, priority, search, "Clear all", …) back
 * into the URL — only the `queue` dimension is externally addressable today.
 * Mirroring every dimension would require a much larger FilterBar rewrite
 * (see AuditLogPage for that fuller pattern) and risks a sync loop fighting
 * the operator's own edits; a one-directional binding on a single param is
 * simpler and avoids that class of bug.
 */
export function AlertsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queueParam = searchParams.getAll('queue')
  // Serialize to a primitive so the effect below reacts to a content
  // change, not the new-array-identity `getAll()` returns on every
  // render. `JSON.stringify` (not `.join(',')`) — `.join(',')` collides
  // on a value containing a comma: `['a,b'].join(',')` and
  // `['a','b'].join(',')` are both `"a,b"`, so a URL going from
  // `?queue=a,b` to `?queue=a&queue=b` would not re-fire the effect and
  // the filter would silently stay on the stale single-element value.
  const queueParamKey = JSON.stringify(queueParam)

  const [filters, setFilters] = useState<AlertFilters>(() => filtersFromQueueParam(queueParam))
  const [sort, setSort] = useState<SortKey[]>([{ field: 'created_at', dir: 'desc' }])
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [userIsAtTop, setUserIsAtTop] = useState(true)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  // Re-seed the queue_id filter whenever the `queue` URL param changes while
  // this page stays mounted — e.g. navigating from one Fraud Queues/Queue
  // Dashboard link to another without an unmount. A useState initializer
  // alone only runs once, so without this effect the second link would be
  // a no-op and the page would silently keep showing the first queue.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local filter state from the external `queue` URL param; bounded to this one field, not a cascade
    setFilters((prev) => ({
      ...prev,
      queue_id: queueParam.length > 0 ? [...queueParam] : undefined,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the serialized param VALUE (queueParamKey), not the new array identity getAll() returns every render
  }, [queueParamKey])

  const queryArgs = useMemo(() => ({ filters, sort }), [filters, sort])
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAlertsInfinite(queryArgs)
  const alerts = useMemo(() => data?.pages.flatMap((p) => p.alerts) ?? [], [data?.pages])
  const total = data?.pages[0]?.total ?? 0

  const selection = useBulkSelection<string>()

  const ws = useAlertsWebSocket({ userIsAtTop })

  // Track scrollTop to decide when to buffer vs. fade-in new alerts.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => setUserIsAtTop(el.scrollTop <= 100)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Document title pings while tab is hidden and new alerts are buffered.
  useEffect(() => {
    if (ws.bufferedNewCount > 0 && document.visibilityState === 'hidden') {
      document.title = `(${ws.bufferedNewCount}) Antifraud — alerts`
    } else {
      document.title = 'Antifraud — alerts'
    }
    return () => {
      document.title = 'Antifraud'
    }
  }, [ws.bufferedNewCount])

  const handleSort = (field: SortKey['field'], multi: boolean) => {
    setSort((prev) => {
      const existing = prev.find((s) => s.field === field)
      if (!multi) {
        if (existing) return [{ field, dir: existing.dir === 'asc' ? 'desc' : 'asc' }]
        return [{ field, dir: 'desc' }]
      }
      // Multi-sort with shift-click — append, or flip if already in the list.
      if (existing) {
        return prev.map((s) =>
          s.field === field ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s,
        )
      }
      return [...prev, { field, dir: 'desc' }]
    })
  }

  const orderedIds = useMemo(() => alerts.map((a) => a.id), [alerts])

  const onToggleSelect = (id: string, range: boolean) => {
    selection.toggle(id, range, orderedIds)
  }

  const onToggleSelectAll = () => {
    if (selection.count === alerts.length) selection.clear()
    else selection.setAll(orderedIds)
  }

  const { focusedIndex } = useQueueKeyboard({
    alertIds: orderedIds,
    onToggleSelect,
    onToggleSelectAll,
    onClearSelection: selection.clear,
    onShowShortcuts: () => setShortcutsOpen(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
  })

  // W3 fix: the `queue` URL param is the source of truth that seeded
  // `filters.queue_id` (see the effect above). If an operator explicitly
  // picks a saved filter or clears filters, but the address bar still
  // says `?queue=<stale-id>`, a page refresh or a shared link would
  // silently re-apply the queue filter the operator just chose to leave —
  // reverting their explicit choice without any visible cause. Clearing
  // the param here keeps the URL truthful to what's on screen. This does
  // NOT generalise to every filter dimension (status/priority/etc. stay
  // one-directional, per the header comment) — only `queue` is ever
  // written into the URL in the first place, so only `queue` needs
  // clearing back out of it.
  const clearQueueUrlParam = () => {
    if (!searchParams.has('queue')) return
    const next = new URLSearchParams(searchParams)
    next.delete('queue')
    setSearchParams(next, { replace: true })
  }

  const handleSelectSavedFilter = (sf: SavedFilter | null) => {
    setActiveSavedId(sf?.id ?? null)
    setFilters(sf?.filters ?? {})
    clearQueueUrlParam()
  }

  const handleClearFilters = () => {
    setFilters({})
    setActiveSavedId(null)
    clearQueueUrlParam()
  }

  const handleResetToMyQueue = () => {
    const my = savedQuery.data?.find((f) => f.id === 'sf-mine')
    if (my) {
      setActiveSavedId('sf-mine')
      setFilters(my.filters)
      clearQueueUrlParam()
    }
  }

  // Chip-level "Remove Queue filter" (FilterBar) — distinct from the
  // generic per-dimension `onChange` clears because this is the one
  // dimension that's also mirrored into the URL; clearing it in filter
  // state alone would leave a stale `?queue=` for a refresh to reapply.
  const handleClearQueueFilter = () => {
    setFilters((prev) => ({ ...prev, queue_id: undefined }))
    clearQueueUrlParam()
  }

  const [saveFilterOpen, setSaveFilterOpen] = useState(false)
  const savedQuery = useSavedFilters()

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-semibold">Alerts</h1>
          <p className="text-xs text-muted-fg">
            {total} total · {alerts.length} loaded
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveIndicator
            paused={ws.paused}
            bufferedCount={ws.bufferedNewCount}
            onTogglePause={() => ws.setPaused(!ws.paused)}
          />
          <ExportCsvButton filters={filters} sort={sort} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Keyboard shortcuts"
            onClick={() => setShortcutsOpen(true)}
          >
            <Keyboard size={16} />
          </Button>
        </div>
      </header>

      <SavedFilterTabs
        activeId={activeSavedId}
        onSelect={handleSelectSavedFilter}
        onCreate={() => setSaveFilterOpen(true)}
      />

      <FilterBar
        filters={filters}
        onChange={(next) => {
          setFilters(next)
          setActiveSavedId(null)
        }}
        onClearAll={handleClearFilters}
        onClearQueue={handleClearQueueFilter}
      />

      <div className="relative flex-1 min-h-0">
        <NewAlertsToast
          count={ws.bufferedNewCount}
          onClick={() => {
            ws.acknowledgeBuffered()
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
        <AlertsTable
          alerts={alerts}
          loading={isLoading}
          error={error}
          filters={filters}
          sort={sort}
          selectedIds={selection.selected}
          focusedIndex={focusedIndex}
          recentlyChangedIds={ws.recentlyChangedIds}
          onSort={handleSort}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={onToggleSelectAll}
          onClearFilters={handleClearFilters}
          onResetToMyQueue={handleResetToMyQueue}
          onRetry={() => void refetch()}
          onLoadMore={() => void fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          scrollContainerRef={scrollRef}
        />
      </div>

      <BulkActionsBar
        selectedIds={Array.from(selection.selected)}
        alerts={alerts}
        onClear={selection.clear}
      />

      <ShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <SaveFilterDialog
        open={saveFilterOpen}
        onOpenChange={setSaveFilterOpen}
        filters={filters}
        onSaved={(saved) => {
          setActiveSavedId(saved.id)
        }}
      />
    </div>
  )
}
